<template>
  <v-dialog v-model="visible" max-width="1100" persistent transition="dialog-bottom-transition">
    <v-card class="rounded-xl border-light" outlined elevation="0">
      <!-- Modal Header -->
      <v-card-title class="d-flex align-center justify-space-between py-3 px-5 border-light-bottom">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-file-document-edit-outline</v-icon>
          <span class="text-h6 font-weight-bold grey--text text--darken-4">
            Nueva Solicitud de Transferencia
          </span>
        </div>
        <v-btn icon small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="py-4 px-5">
        <!-- 1. Datos de Cabecera -->
        <v-row dense>
          <v-col cols="12">
            <span class="text-caption font-weight-bold grey--text text--darken-2 text-uppercase">
              1. Datos de Cabecera
            </span>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <s-select-area
              disabled
              label="Área Solicitante *"
              :text="userContractAreaName"
              v-model="form.AreID"
              full
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <s-date disabled label="Fecha *" v-model="form.WtrDate" />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <s-select-definition
              disabled
              :def="1062"
              label="Turno *"
              v-model="form.TypeShift"
            />
          </v-col>
          <v-col cols="12" sm="6" md="5">
            <s-text disabled label="Encargado *" v-model="form.WtrManager" readonly />
          </v-col>
          <v-col cols="12">
            <s-textarea label="Observaciones / Comentarios" v-model="form.WtrObservation" />
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- 2. Detalle de Grilla (Agregar Material) -->
        <v-row dense class="align-center mb-3">
          <v-col cols="12">
            <span class="text-caption font-weight-bold grey--text text--darken-2 text-uppercase">
              2. Agregar Materiales a la Solicitud
            </span>
          </v-col>
          <v-col cols="12" sm="6" md="6">
            <s-article-search
              label="Buscar Material SAP *"
              v-model="articleSearchModel"
              @selected="onArticleSelected"
              clearable
            />
          </v-col>
          <v-col cols="12" sm="2" md="2">
            <s-text label="Código" v-model="tempItem.ArtCode" readonly />
          </v-col>
          <v-col cols="12" sm="2" md="2">
            <s-text label="U.M" v-model="tempItem.UnitMeasurement" readonly />
          </v-col>
          <v-col cols="12" sm="2" md="2">
            <s-text label="Stock SAP" :value="tempItem.ArtStock != null ? tempItem.ArtStock : '-'" readonly />
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <s-text
              label="Almacén Origen (Actual)"
              :value="tempItemFromWhsDisplay"
              readonly
            />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <s-select
              label="Almacén Destino *"
              :items="warehouseList"
              item-text="WhsName"
              item-value="WhsCode"
              v-model="tempItem.ToWhsCode"
              clearable
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <s-text
              label="Cantidad Solicitada *"
              v-model.number="tempItem.WtdQuantityRequested"
              type="number"
              @input="onQuantityInput"
            />
          </v-col>
          <v-col cols="12" sm="6" md="2" class="d-flex align-center">
            <v-btn
              color="primary"
              class="rounded-lg text-none font-weight-bold"
              elevation="0"
              block
              :disabled="!isTempItemValid"
              @click="addItem"
            >
              <v-icon left small>mdi-plus</v-icon>
              Añadir
            </v-btn>
          </v-col>
        </v-row>

        <!-- Grilla de Ítems -->
        <v-data-table
          :headers="headers"
          :items="form.Details"
          dense
          class="elevation-0 border-light rounded-lg"
          hide-default-footer
          :items-per-page="-1"
          no-data-text="No hay materiales agregados a la solicitud"
        >
          <template v-slot:[`item.WtdNumLine`]="{ index }">
            {{ index + 1 }}
          </template>
          <template v-slot:[`item.ArtStock`]="{ item }">
            <span class="font-weight-bold grey--text text--darken-3">
              {{ item.ArtStock != null ? Number(item.ArtStock).toFixed(2) : '-' }}
            </span>
          </template>
          <template v-slot:[`item.FromWhsCode`]="{ item }">
            <span>{{ getWarehouseName(item.FromWhsCode) }}</span>
          </template>
          <template v-slot:[`item.ToWhsCode`]="{ item }">
            <span class="font-weight-medium">{{ getWarehouseName(item.ToWhsCode) }}</span>
          </template>
          <template v-slot:[`item.WtdQuantityRequested`]="{ item }">
            <span class="font-weight-bold primary--text">
              {{ Number(item.WtdQuantityRequested).toFixed(2) }}
            </span>
          </template>
          <template v-slot:[`item.actions`]="{ index }">
            <v-btn icon small color="error" @click="removeItem(index)">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>

      <v-divider></v-divider>

      <!-- Botones de Acción -->
      <v-card-actions class="py-3 px-5">
        <span class="text-caption grey--text text--darken-1">
          Total de líneas: <strong>{{ form.Details.length }}</strong>
        </span>
        <v-spacer></v-spacer>
        <v-btn text class="text-none rounded-lg" @click="close">
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          class="text-none rounded-lg font-weight-bold px-5"
          elevation="0"
          :loading="saving"
          :disabled="!isFormValid"
          @click="saveRequest"
        >
          <v-icon left small>mdi-content-save</v-icon>
          Registrar Solicitud
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import SSelectArea from "@/components/Utils/SSelectArea.vue";
import SDate from "@/components/Utils/SDate.vue";
import SSelectDefinition from "@/components/Utils/SSelectDefinition.vue";
import SText from "@/components/Utils/SText.vue";
import SSelect from "@/components/Utils/SSelect.vue";
import SArticleSearch from "@/components/Utils/Logistics/SArticleSearch.vue";
import _sWarehouseTransfer from "@/services/Logistics/WarehouseTransferService";

export default {
  name: "WarehouseTransferRequestModal",
  components: {
    SSelectArea,
    SDate,
    SSelectDefinition,
    SText,
    SSelect,
    SArticleSearch,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    warehouses: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      saving: false,
      articleSearchModel: null,
      warehouseList: [],
      form: {
        WtrID: 0,
        WtrDocumentNumber: "",
        WtrDate: new Date().toISOString().substring(0, 10),
        AreID: null,
        TypeShift: 1, // DIA por defecto
        WtrManager: "",
        FromWhsCode: null,
        ToWhsCode: null,
        WtrObservation: "",
        Details: [],
      },
      tempItem: {
        ArtCode: "",
        WtdDescription: "",
        UnitMeasurement: "",
        ArtStock: null,
        FromWhsCode: null,
        ToWhsCode: null,
        WtdQuantityRequested: null,
      },
      headers: [
        { text: "N°", value: "WtdNumLine", width: 60, align: "center", sortable: false },
        { text: "Código", value: "ArtCode", width: 120, sortable: false },
        { text: "Descripción del Material", value: "WtdDescription", sortable: false },
        { text: "U.M.", value: "UnitMeasurement", width: 80, align: "center", sortable: false },
        { text: "Stock SAP", value: "ArtStock", width: 100, align: "right", sortable: false },
        { text: "Almacén Origen", value: "FromWhsCode", width: 160, sortable: false },
        { text: "Almacén Destino", value: "ToWhsCode", width: 160, sortable: false },
        { text: "Cant. Solicitada", value: "WtdQuantityRequested", width: 130, align: "right", sortable: false },
        { text: "Acciones", value: "actions", width: 70, align: "center", sortable: false },
      ],
    };
  },
  computed: {
    visible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    tempItemFromWhsDisplay() {
      if (!this.tempItem.FromWhsCode) return "";
      return this.getWarehouseName(this.tempItem.FromWhsCode);
    },
    isTempItemValid() {
      return (
        Boolean(this.tempItem.ArtCode) &&
        Boolean(this.tempItem.WtdDescription) &&
        Number(this.tempItem.WtdQuantityRequested) > 0 &&
        Boolean(this.tempItem.FromWhsCode) &&
        Boolean(this.tempItem.ToWhsCode)
      );
    },
    isFormValid() {
      return (
        Boolean(this.form.AreID) &&
        Boolean(this.form.WtrDate) &&
        Boolean(this.form.TypeShift) &&
        Boolean(this.form.WtrManager) &&
        this.form.Details.length > 0
      );
    },
    userContractAreaName() {
      const userInfo = this.$fun.getUserInfo();
      return userInfo?.CtrContract?.AreName || "";
    },
  },
  watch: {
    warehouses: {
      immediate: true,
      handler(val) {
        if (val && val.length > 0) {
          this.warehouseList = val;
        }
      },
    },
    value(val) {
      if (val) {
        this.initializeForm();
      }
    },
  },
  methods: {
    initializeForm() {
      const userInfo = this.$fun.getUserInfo();
      const contractArea = userInfo?.CtrContract?.AreID || null;
      const contractShift = userInfo?.CtrContract?.TypeTurno || userInfo?.CtrContract?.TypeShift || 1;

      this.form = {
        WtrID: 0,
        WtrDocumentNumber: "",
        WtrDate: new Date().toISOString().substring(0, 10),
        AreID: contractArea,
        TypeShift: contractShift,
        WtrManager: userInfo ? userInfo.NtpFullName : "",
        FromWhsCode: null,
        ToWhsCode: null,
        WtrObservation: "",
        Details: [],
      };
      this.resetTempItem();
    },
    resetTempItem() {
      this.articleSearchModel = null;
      this.tempItem = {
        ArtCode: "",
        WtdDescription: "",
        UnitMeasurement: "",
        ArtStock: null,
        FromWhsCode: null,
        ToWhsCode: null,
        WtdQuantityRequested: null,
      };
    },
    onArticleSelected(art) {
      if (art) {
        const stock = Number(art.ArtStock || 0);
        if (stock <= 0) {
          this.$fun.alert("El material seleccionado no cuenta con Stock disponible en SAP.", "warning");
          this.resetTempItem();
          return;
        }

        this.tempItem.ArtCode = art.ArtCode;
        this.tempItem.WtdDescription = art.ArtName || art.ArtDescription;
        this.tempItem.UnitMeasurement = art.UnitOfMeasuarement || art.UnitMeasurement || "UND";
        this.tempItem.ArtStock = stock;
        this.tempItem.FromWhsCode = art.WhsCode || "01";
        this.tempItem.WtdQuantityRequested = null;
        if (!this.tempItem.ToWhsCode && this.warehouseList.length > 0) {
          const otherWhs = this.warehouseList.find((w) => w.WhsCode !== this.tempItem.FromWhsCode);
          this.tempItem.ToWhsCode = otherWhs ? otherWhs.WhsCode : this.warehouseList[0].WhsCode;
        }
      }
    },
    onQuantityInput(val) {
      if (this.tempItem.ArtStock != null && this.tempItem.ArtStock > 0) {
        if (Number(val) > Number(this.tempItem.ArtStock)) {
          this.$fun.alert(`La cantidad no puede superar el stock disponible (${this.tempItem.ArtStock}).`, "warning");
          this.$nextTick(() => {
            this.tempItem.WtdQuantityRequested = Number(this.tempItem.ArtStock);
          });
        }
      }
    },
    addItem() {
      if (!this.isTempItemValid) return;

      if (this.tempItem.FromWhsCode === this.tempItem.ToWhsCode) {
        this.$fun.alert("El almacén de destino no puede ser igual al almacén de origen.", "warning");
        return;
      }

      const requestedQty = Number(this.tempItem.WtdQuantityRequested);
      if (this.tempItem.ArtStock != null && requestedQty > Number(this.tempItem.ArtStock)) {
        this.$fun.alert(`No puede solicitar más del stock disponible (${this.tempItem.ArtStock}).`, "warning");
        return;
      }

      const existing = this.form.Details.find((d) => d.ArtCode === this.tempItem.ArtCode);
      if (existing) {
        this.$fun.alert("El material ya se encuentra en la lista. Modifique su cantidad en la grilla.", "warning");
        return;
      }

      this.form.Details.push({
        WtdNumLine: this.form.Details.length + 1,
        ArtCode: this.tempItem.ArtCode,
        WtdDescription: this.tempItem.WtdDescription,
        UnitMeasurement: this.tempItem.UnitMeasurement,
        ArtStock: this.tempItem.ArtStock,
        FromWhsCode: this.tempItem.FromWhsCode,
        ToWhsCode: this.tempItem.ToWhsCode,
        WtdQuantityRequested: requestedQty,
      });

      this.resetTempItem();
    },
    removeItem(index) {
      this.form.Details.splice(index, 1);
    },
    getWarehouseName(code) {
      const wh = this.warehouseList.find((w) => w.WhsCode === code);
      return wh ? `${wh.WhsCode} - ${wh.WhsName}` : (code || "-");
    },
    saveRequest() {
      if (!this.isFormValid) {
        this.$fun.alert("Complete los campos obligatorios y agregue al menos un material.", "warning");
        return;
      }

      if (this.form.Details.length > 0) {
        this.form.FromWhsCode = this.form.Details[0].FromWhsCode || "01";
        this.form.ToWhsCode = this.form.Details[0].ToWhsCode || "01";
      }

      this.$fun.alert("¿Desea registrar esta solicitud de transferencia?", "question").then((res) => {
        if (res.value) {
          this.saving = true;
          _sWarehouseTransfer
            .save(this.form, this.$fun.getUserID())
            .then((resp) => {
              this.saving = false;
              this.$fun.alert("Solicitud registrada correctamente.", "success");
              this.$emit("saved", resp.data);
              this.close();
            })
            .catch((err) => {
              this.saving = false;
              const msg = err.response?.data?.Message || err.message || "Error al registrar la solicitud.";
              this.$fun.alert(msg, "error");
            });
        }
      });
    },
    close() {
      this.visible = false;
      this.$emit("close");
    },
  },
};
</script>

